import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import WhatsAppButton from "@/components/WhatsAppButton";
import ContactSection from "@/components/sections/ContactSection";
import { useLang } from "@/context/LanguageContext";
import useSeo from "@/hooks/useSeo";
import {
  ArrowUpRight, UserCheck, Wrench, PhoneCall, Handshake, Users2,
  Sparkles, Shield, Network,
} from "lucide-react";

// Direkt-Links: jede Leistung führt zur passenden Unterseite
const LINKS = {
  "01": "/ankauf",
  "02": "/fahrzeuge",
  "03": "/finanzierung",
  "04": "/finanzierung",
  "05": "/auto-abo",
  "06": "/mietwagen",
  "07": "/inzahlungnahme",
  "08": "/kontakt",
  "09": "/kontakt",
  "10": "/kontakt",
  "11": "/service",
  "12": "/service",
  "13": "/service",
  "14": "/kontakt",
  "15": "/kontakt",
};

const KOMPLETT_ICONS = [UserCheck, Wrench, PhoneCall, Handshake, Users2];
const EXKL_ICONS = [Sparkles, Shield, Network];

export default function Leistungen() {
  const { t } = useLang();
  const location = useLocation();

  useSeo({
    title: "Leistungen · 15 Premium-Services | DNA Automotive Köln",
    description: "Alle Leistungen von DNA Automotive Köln: Fahrzeugankauf & Verkauf, Finanzierung, Leasing, Auto-Abo, Mietwagen, Inzahlungnahme, Aufbereitung, Folierung, Zulassungsservice, Fuhrparkservice und mehr.",
    path: "/leistungen",
    breadcrumbs: [{ name: "Leistungen", url: "/leistungen" }],
  });

  useEffect(() => {
    if (location.hash) {
      setTimeout(() => {
        document.getElementById(location.hash.slice(1))?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 200);
    } else {
      window.scrollTo(0, 0);
    }
  }, [location.hash]);

  return (
    <div className="bg-obsidian min-h-screen" data-testid="leistungen-page">
      <Header />

      {/* Hero */}
      <section className="pt-36 pb-16 px-6 md:px-10 grain">
        <div className="max-w-[1600px] mx-auto">
          <p className="text-brand-bright text-xs uppercase tracking-[0.3em] mb-4">{t.services.overline}</p>
          <h1 className="font-heading font-black uppercase tracking-tighter text-white text-5xl md:text-7xl leading-[0.9] mb-6">
            {t.services.title}
          </h1>
          {t.services.intro && (
            <p className="text-zinc-400 leading-relaxed text-base md:text-lg max-w-3xl">{t.services.intro}</p>
          )}
        </div>
      </section>

      {/* Alle 15 Leistungen – klickbar mit Direkt-Links */}
      <section className="px-6 md:px-10 pb-24" data-testid="leistungen-list">
        <div className="max-w-[1600px] mx-auto border-t border-white/10">
          {t.services.items.map((item, idx) => (
            <motion.div
              key={item.n}
              id={`service-${item.n}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5, delay: (idx % 3) * 0.05, ease: [0.22, 1, 0.36, 1] }}
              className="scroll-mt-28"
            >
              <Link
                to={LINKS[item.n] || "/kontakt"}
                className="group relative flex border-b border-white/10 py-10 md:py-12 hover:bg-white/[0.02] transition-colors duration-500"
                data-testid={`leistung-${item.n}`}
              >
                <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8 items-start w-full px-2 md:px-4">
                  <span className="md:col-span-3 font-heading font-black text-6xl md:text-7xl leading-none text-zinc-800 group-hover:text-brand-bright transition-colors duration-500 select-none">
                    {item.n}
                  </span>
                  <div className="md:col-span-8">
                    <h2 className="font-heading font-extrabold uppercase tracking-tight text-2xl md:text-3xl text-white mb-3 group-hover:text-brand-bright transition-colors duration-500">
                      {item.t}
                    </h2>
                    <p className="text-zinc-400 text-sm md:text-base leading-relaxed max-w-xl">{item.d}</p>
                  </div>
                  <span className="hidden md:flex md:col-span-1 justify-end pt-2 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-500">
                    <ArrowUpRight className="w-7 h-7 text-brand-bright" />
                  </span>
                </div>
                <div className="absolute bottom-0 left-0 h-px w-0 bg-brand-light group-hover:w-full transition-all duration-700" />
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Komplettpaket */}
      {t.services.komplettItems && (
        <section className="px-6 md:px-10 pb-24" data-testid="services-komplett">
          <div className="max-w-[1600px] mx-auto">
            <p className="text-brand-bright text-xs uppercase tracking-[0.3em] mb-3">{t.services.komplettOverline}</p>
            <h2 className="font-heading font-black uppercase tracking-tighter text-white text-3xl md:text-5xl leading-[0.95] mb-10">
              {t.services.komplettTitle}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {t.services.komplettItems.map((k, i) => {
                const Icon = KOMPLETT_ICONS[i] || Sparkles;
                return (
                  <div
                    key={k.t}
                    className="bg-obsidian-surface border border-white/10 p-6 hover:border-brand-light/50 transition-colors duration-500 group"
                    data-testid={`komplett-${i}`}
                  >
                    <Icon className="w-8 h-8 text-brand-bright mb-5 group-hover:scale-110 transition-transform" />
                    <h3 className="font-heading font-extrabold uppercase tracking-tight text-white text-base leading-snug mb-3">{k.t}</h3>
                    <p className="text-zinc-400 text-xs leading-relaxed">{k.d}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Exklusivität statt Masse */}
      {t.services.exklPillars && (
        <section className="px-6 md:px-10 pb-28" data-testid="services-exkl">
          <div className="max-w-[1600px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
            <div className="lg:col-span-5">
              <p className="text-brand-bright text-xs uppercase tracking-[0.3em] mb-3">{t.services.exklOverline}</p>
              <h2 className="font-heading font-black uppercase tracking-tighter text-white text-3xl md:text-5xl leading-[0.95] mb-6">
                {t.services.exklTitle}
              </h2>
              <p className="text-zinc-400 leading-relaxed text-base">{t.services.exklText}</p>
            </div>
            <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-3 gap-4">
              {t.services.exklPillars.map((p, i) => {
                const Icon = EXKL_ICONS[i] || Sparkles;
                return (
                  <div key={p.t} className="border-t-2 border-brand-light pt-5" data-testid={`exkl-${i}`}>
                    <Icon className="w-6 h-6 text-brand-bright mb-4" />
                    <h3 className="font-heading font-extrabold uppercase tracking-tight text-white text-base leading-snug mb-2">{p.t}</h3>
                    <p className="text-zinc-400 text-sm leading-relaxed">{p.d}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      <ContactSection />
      <Footer />
      <WhatsAppButton />
    </div>
  );
}
